import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon } from 'lucide-react';
import { Container, Section, Kicker, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { MeetingScheduler } from '../components/MeetingScheduler';
import { ASSESSMENT_QUESTIONS, DIMENSION_LABELS, scoreAssessment } from '../data/assessment';
import { submitLead, emailAssessmentResult } from '../lib/leads';
import { MAIL } from '../lib/config';
import { track } from '../lib/analytics';
type Stage = 'intro' | 'questions' | 'gate' | 'result';
export function Assessment() {
  const [stage, setStage] = React.useState<Stage>('intro');
  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, number>>({});
  const [email, setEmail] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [emailed, setEmailed] = React.useState(false);
  const reduced = useReducedMotion();
  const question = ASSESSMENT_QUESTIONS[index];
  const answered = Object.keys(answers).length;
  const progress = Math.round(answered / ASSESSMENT_QUESTIONS.length * 100);
  const result = React.useMemo(() => scoreAssessment(answers), [answers]);
  const choose = (score: number) => {
    setAnswers(current => ({
      ...current,
      [question.id]: score
    }));
    window.setTimeout(() => {
      if (index === ASSESSMENT_QUESTIONS.length - 1) {
        setStage('gate');
      } else {
        setIndex(value => value + 1);
      }
    }, 180);
  };
  const reveal = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const outcome = await submitLead({
      email,
      organisation,
      consent,
      source: 'ai-readiness-assessment',
      line: 'ai'
    });
    setSubmitting(false);
    if (!outcome.ok) {
      setError(outcome.error ?? 'Something went wrong. Please try again.');
      return;
    }
    const sentResult = await emailAssessmentResult({
      email,
      organisation,
      total: result.total,
      band: result.band.name,
      byDimension: result.byDimension,
      nextSteps: result.band.nextSteps.map(step => ({
        title: step.title,
        packageName: step.packageName
      }))
    });
    setEmailed(sentResult.ok);
    track('assessment_completed', {
      score: result.total,
      band: result.band.name
    });
    setStage('result');
  };
  return <>
      <section className="surface-dark" data-id="element-485" data-mp="2op5b9fygyf83">
        <Container className="py-14 sm:py-20" data-id="element-486" data-mp="1i6yz6kism5hx">
          <Kicker data-id="element-487" data-mp="1i4mdbw9wzazl">Cavalla AI</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-488" data-mp="1d74a8mikvpzi">
            AI readiness assessment
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-489" data-mp="3oswfr7vpg0nj">
            Twelve questions across data, skills, use cases, governance and budget. It takes about four minutes
            and gives you a score out of one hundred with three recommended next steps.
          </p>
        </Container>
      </section>

      <Section data-id="element-490" data-mp="cdkpmtf060iy">
        {stage === 'intro' && <div className="mx-auto max-w-2xl" data-id="element-491" data-mp="ccstnhhhu0s5">
            <h2 className="font-display text-[28px] leading-tight text-ink" data-id="element-492" data-mp="gf5xu1gldqmt">Before you start</h2>
            <p className="mt-4 text-[16px] text-body" data-id="element-493" data-mp="le93no4ec0vu">
              Answer as your organisation is today, not as you intend it to be. The value of the score sits in its
              honesty. Where you are unsure, choose the lower option.
            </p>
            <ul className="mt-6 space-y-3 text-[15px] text-body" data-id="element-494" data-mp="2n6x7nzov6sco">
              {Object.values(DIMENSION_LABELS).map(label => <li key={label} className="flex gap-3" data-id="element-495" data-mp="2s6jo0hzzoopp">
                  <span aria-hidden="true" className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-gold" data-id="element-496" data-mp="mfd5pcbdwdj4" />
                  {label}
                </li>)}
            </ul>
            <div className="mt-8" data-id="element-497" data-mp="vhe5afu3bbny">
              <Button size="lg" onClick={() => setStage('questions')} data-id="element-498" data-mp="vgkwl9bkmc36">
                Begin the assessment
              </Button>
            </div>
          </div>}

        {stage === 'questions' && <div className="mx-auto max-w-2xl" data-id="element-499" data-mp="3q6q3y5w021j">
            <div className="flex items-center justify-between text-small text-muted" data-id="element-500" data-mp="3uwrj3nfi5k17">
              <span data-id="element-501" data-mp="ci08lq4z7mtr">
                Question {index + 1} of {ASSESSMENT_QUESTIONS.length}
              </span>
              <span data-id="element-502" data-mp="7r0dmqff3etw">{DIMENSION_LABELS[question.dimension]}</span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-mist" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Assessment progress" data-id="element-503" data-mp="3g73jcqmyytfl">
              <motion.div className="h-full rounded-full bg-gold" initial={false} animate={{
            width: `${Math.max(progress, 4)}%`
          }} transition={{
            duration: reduced ? 0 : 0.3,
            ease: 'easeOut'
          }} data-id="element-504" data-mp="36s9tep3s5l5s" />
            </div>

            <motion.div key={question.id} initial={reduced ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: 'easeOut'
        }} className="mt-8" data-id="element-505" data-mp="3ksb5l0phwf0k">
              <fieldset data-id="element-506" data-mp="3atvgf3a13ftp">
                <legend className="font-display text-[24px] leading-snug text-ink sm:text-[28px]" data-id="element-507" data-mp="3ft805f5ls6s2">
                  {question.question}
                </legend>
                <div className="mt-6 space-y-3" data-id="element-508" data-mp="1zdewawnj4b0i">
                  {question.options.map(option => {
                const selected = answers[question.id] === option.score;
                return <button key={option.label} type="button" onClick={() => choose(option.score)} aria-pressed={selected} className={`w-full rounded-sm border px-5 py-4 text-left text-[16px] transition-colors ${selected ? 'border-royal bg-mist text-ink' : 'border-line text-body hover:border-royal/40 hover:bg-mist/50'}`} data-id="element-509" data-mp="auj4uac3mpkt">
                        {option.label}
                      </button>;
              })}
                </div>
              </fieldset>
            </motion.div>

            {index > 0 && <button type="button" onClick={() => setIndex(value => value - 1)} className="mt-8 inline-flex items-center gap-2 text-[15px] text-royal underline-offset-4 hover:underline" data-id="element-510" data-mp="1ck6265hcpy4l">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-511" data-mp="1k3p0rk32vzya" />
                Previous question
              </button>}
          </div>}

        {stage === 'gate' && <div className="mx-auto max-w-xl rounded-md border border-line bg-champagne p-7 sm:p-9" data-id="element-512" data-mp="1tujnsqo5f3ks">
            <Kicker data-id="element-513" data-mp="w6ronr5j5sak">Your result is ready</Kicker>
            <h2 className="mt-4 font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-514" data-mp="w82tluapp639">
              Where should we send it
            </h2>
            <p className="mt-3 text-[15px] text-body" data-id="element-515" data-mp="163wd5xmulzuj">
              We will show your score and next steps on this page straight away, and email you a copy from{' '}
              {MAIL.from} so you can take it into a leadership discussion.
            </p>
            <form onSubmit={reveal} className="mt-7 space-y-5" data-id="element-516" data-mp="2hlp1p0aj3lsg">
              <Field id="asm-email" label="Work email" required error={error ?? undefined} data-id="element-517" data-mp="2mhcsfk4nsn02">
                <input id="asm-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-518" data-mp="37nrnkyw5h8xn" />
              </Field>
              <Field id="asm-org" label="Organisation" required data-id="element-519" data-mp="3cjzz82b52k02">
                <input id="asm-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-520" data-mp="3qbmvw38ztujd" />
              </Field>
              <ConsentCheckbox id="asm-consent" checked={consent} onChange={setConsent} data-id="element-521" data-mp="3vcu3o2b4ytq1">
                I am happy for Cavalla Solution to email me my result and related insights. See our{' '}
                <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-522" data-mp="20zuhtrg5x3ab">
                  privacy notice
                </Link>
                .
              </ConsentCheckbox>
              <Button type="submit" size="lg" disabled={submitting} data-id="element-523" data-mp="le0ocxhilznt">
                {submitting ? 'Preparing your result' : 'Show my score'}
              </Button>
            </form>
          </div>}

        {stage === 'result' && <div className="mx-auto max-w-3xl" data-id="element-524" data-mp="bn61bqwg2w1b">
            <div className="rounded-md border border-line bg-mist p-7 sm:p-10" data-id="element-525" data-mp="3tu3ugyszbga1">
              <Kicker data-id="element-526" data-mp="2pdlmlmt9mde">Your result</Kicker>
              <div className="mt-5 flex flex-wrap items-end gap-6" data-id="element-527" data-mp="2q5gfu8l3g2m">
                <p className="font-display text-[64px] leading-none text-royal" data-id="element-528" data-mp="3jy0v20pihvqs">{result.total}</p>
                <div data-id="element-529" data-mp="3ov967hc8szij">
                  <p className="text-small text-muted" data-id="element-530" data-mp="3owkhe280p9jo">out of one hundred</p>
                  <p className="font-display text-[26px] leading-tight text-ink" data-id="element-531" data-mp="1i6ns3h73txxd">{result.band.name}</p>
                </div>
              </div>
              <p className="mt-6 max-w-prose text-[16px] text-body" data-id="element-532" data-mp="1n2l6hhyt4u7i">{result.band.description}</p>

              <p className="mt-6 flex items-start gap-2.5 text-[14px] text-muted" data-id="element-533" data-mp="36c7trqsp8q6a">
                <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-534" data-mp="3b6kgqatdwjms" />
                {emailed ? <span data-id="element-535" data-mp="2wea6p6mkszf9">
                    A copy is on its way to <span className="text-ink" data-id="element-536" data-mp="31g984ayr9m9k">{email}</span> from {MAIL.from}. If it
                    does not arrive, please check your spam folder.
                  </span> : <span data-id="element-537" data-mp="3u2u1ti2peaga">
                    We could not email your copy just now. Your full result is shown below, and we will follow it
                    up with you directly.
                  </span>}
              </p>

              <div className="mt-8 space-y-4" data-id="element-538" data-mp="3k7saqgovayb2">
                {result.byDimension.map(entry => {
              const percentage = Math.round(entry.score / entry.max * 100);
              return <div key={entry.dimension} data-id="element-539" data-mp="qsxajiwbcg4c">
                      <div className="flex items-baseline justify-between text-[14px]" data-id="element-540" data-mp="qrm7xmelteet">
                        <span className="text-ink" data-id="element-541" data-mp="lsrezrmxa9gz">{DIMENSION_LABELS[entry.dimension]}</span>
                        <span className="text-muted" data-id="element-542" data-mp="ztvj8ehp5bt9">{percentage}%</span>
                      </div>
                      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-white" data-id="element-543" data-mp="vrqqrj2zil87">
                        <motion.div className="h-full rounded-full bg-gold" initial={reduced ? false : {
                    width: 0
                  }} animate={{
                    width: `${percentage}%`
                  }} transition={{
                    duration: reduced ? 0 : 0.6,
                    ease: 'easeOut'
                  }} data-id="element-544" data-mp="81mobf1j8ris" />
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <h2 className="mt-12 font-display text-[26px] text-ink" data-id="element-545" data-mp="3gi7nk44d468p">Three recommended next steps</h2>
            <ol className="mt-6 space-y-4" data-id="element-546" data-mp="1jhlf9lj9bkg7">
              {result.band.nextSteps.map((step, stepIndex) => <li key={step.title} className="rounded-md border border-line p-6" data-id="element-547" data-mp="1og6crd74jq0y" data-mpi={stepIndex}>
                  <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-548" data-mp="1t7el05wpx5cj">
                    <div data-id="element-549" data-mp="1t9r3qlaavjqn">
                      <p className="font-display text-[13px] text-gold" data-id="element-550" data-mp="1y710yn3xkzp1">0{stepIndex + 1}</p>
                      <h3 className="mt-2 font-display text-[21px] leading-snug text-ink" data-id="element-551" data-mp="10izb1xsg1d82">{step.title}</h3>
                      <p className="mt-2 max-w-prose text-[15px] text-body" data-id="element-552" data-mp="15haik1v9nbn4">{step.detail}</p>
                    </div>
                    <div className="shrink-0" data-id="element-553" data-mp="1afvkc5q623do">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted" data-id="element-554" data-mp="1aj9o9iehakn8">
                        Engagement
                      </p>
                      <p className="mt-1 text-[15px] font-medium text-royal" data-id="element-555" data-mp="2lvahpamaex9b">{step.packageName}</p>
                    </div>
                  </div>
                </li>)}
            </ol>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-556" data-mp="f6iauf3okn2d">
              <Button to="/services/ai" variant="secondary" size="lg" data-id="element-557" data-mp="a87104ddyck5">
                Read about Cavalla AI
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-558" data-mp="5h8j50cr16es" />
              </Button>
            </div>

            <div className="mt-12 rounded-md border border-line p-6 sm:p-8" data-id="element-559" data-mp="i4781yfwukr">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-560" data-mp="3rpgr4oqpjm4d">Next step</p>
              <h2 className="mt-4 font-display text-[24px] leading-snug text-ink sm:text-[28px]" data-id="element-561" data-mp="3nndallp6lrc8">
                Take your score into a conversation
              </h2>
              <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-562" data-mp="3dpfgihkn50bp">
                Thirty minutes with our founder, who will have your result to hand. Choose a slot that suits
                your time zone.
              </p>
              <MeetingScheduler line="ai" className="mt-6" data-id="element-563" data-mp="1bxsw4yv983o7" />
            </div>

            <Callout className="mt-8" data-id="element-564" data-mp="9kxrp0n5j7cl">
              This score is an indicative guide produced from your own answers. It is not an audit, and Cavalla
              Solution is not a financial adviser.
            </Callout>
          </div>}
      </Section>
    </>;
}